import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/config/api'
import type {
  ThreadAnalysisCombinedResponse,
  AnalysisStartResponse,
  ChurnAnalysis,
  AnalysisStatus,
} from '@/types'

const POLL_INTERVAL = 2000 // 2초마다 폴링

// Query keys
export const analysisKeys = {
  all: ['analysis'] as const,
  thread: (threadId: number) => [...analysisKeys.all, 'thread', threadId] as const,
}

/**
 * React Query based hook for thread analysis.
 * - Automatically polls when status is 'received' or 'processing'
 * - Stops polling when 'completed' or 'failed'
 * - Uses single combined endpoint for efficiency
 */
export function useThreadAnalysis(threadId: number) {
  const queryClient = useQueryClient()
  const [isStarting, setIsStarting] = useState(false)

  // Main query - polls for status and result
  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: analysisKeys.thread(threadId),
    queryFn: async (): Promise<ThreadAnalysisCombinedResponse> => {
      const { data } = await api.get<ThreadAnalysisCombinedResponse>(
        `/api/analysis/thread/${threadId}`
      )
      return data
    },
    // Only poll when analysis is in progress
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'received' || status === 'processing') {
        return POLL_INTERVAL
      }
      return false // Stop polling
    },
    // Don't retry on 404 (analysis not started)
    retry: (failureCount, error) => {
      if ((error as Error & { response?: { status: number } })?.response?.status === 404) {
        return false
      }
      return failureCount < 2
    },
  })

  // Mutation to start analysis
  const startMutation = useMutation({
    mutationFn: async (forceRefresh: boolean) => {
      const { data } = await api.post<AnalysisStartResponse>(
        `/api/analysis/thread/${threadId}`,
        { force_refresh: forceRefresh }
      )
      return data
    },
    onMutate: () => {
      setIsStarting(true)
    },
    onSuccess: (response) => {
      // If completed immediately (cache hit), invalidate to fetch result
      if (response.status === 'completed') {
        queryClient.invalidateQueries({ queryKey: analysisKeys.thread(threadId) })
      } else {
        // Set initial status and start polling via refetch
        queryClient.setQueryData<ThreadAnalysisCombinedResponse>(
          analysisKeys.thread(threadId),
          {
            thread_id: threadId,
            user_id: null,
            status: response.status,
            error_message: null,
            updated_at: response.updated_at,
            result: null,
          }
        )
      }
    },
    onSettled: () => {
      setIsStarting(false)
    },
  })

  // Derive state from query data
  const status: AnalysisStatus | 'idle' = data?.status ?? 'idle'
  const isPolling = status === 'received' || status === 'processing'
  const is404 = (queryError as Error & { response?: { status: number } })?.response?.status === 404

  // Compute state for UI
  let state: 'idle' | 'checking' | 'polling' | 'completed' | 'error'
  if (isLoading || isStarting) {
    state = 'checking'
  } else if (is404) {
    state = 'idle'
  } else if (isPolling) {
    state = 'polling'
  } else if (status === 'completed') {
    state = 'completed'
  } else if (status === 'failed' || queryError) {
    state = 'error'
  } else {
    state = 'idle'
  }

  // Error message
  const error = data?.error_message ?? (queryError ? (queryError as Error).message : null)

  // Analyze function
  const analyze = useCallback(
    (forceRefresh = false) => {
      startMutation.mutate(forceRefresh)
    },
    [startMutation]
  )

  return {
    // Data
    result: data?.result ?? null,
    status: data?.status ?? null,
    // State
    state,
    loading: state === 'checking' || state === 'polling',
    error,
    // Actions
    analyze,
    refetch,
  }
}

export function useChurnAnalysis(userId: string) {
  const [analysis, setAnalysis] = useState<ChurnAnalysis | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const analyze = useCallback(
    async (forceRefresh = false) => {
      setAnalyzing(true)
      setError(null)
      try {
        const { data } = await api.post<ChurnAnalysis>(`/api/analysis/churn/${userId}`, {
          force_refresh: forceRefresh,
        })
        setAnalysis(data)
      } catch (err) {
        setError((err as Error).message)
        console.error('Failed to analyze churn:', err)
      } finally {
        setAnalyzing(false)
      }
    },
    [userId]
  )

  return { analysis, analyze, analyzing, error }
}
